import {
  FilterKeyValueType,
  IFilter,
  IOption,
} from "@/domain/entities/filter/filter.types";
import {
  IAnswerProp,
  ILineDemandSurvey,
  ILineDemandSurveyAnswer,
  IQuestionOption,
  IQuestionProp,
} from "@/domain/entities/survey";
import { IUserInfo } from "@/domain/entities/user";

function mapOption(option: IQuestionOption): IOption {
  return {
    key: option.uid,
    parentKey: option.parentUid,
    value: option.displayText,
    name: option.displayText,
  };
}

/**
 * 현재 질문(question)의 options 중 parentUid를 모아서,
 * 그 parentUid를 옵션 uid로 가진 이전 질문(prev)의 key를 찾아 반환.
 */
function findParentKey(
  allProps: IQuestionProp[],
  current: IQuestionProp
): string | undefined {
  // 1) parentUid가 정의된 옵션만 추출
  const parentUids = new Set<string>(
    current.options
      ?.map((option) => option.parentUid)
      .filter((uid): uid is string => !!uid) // undefined 제거
  );
  if (parentUids.size === 0) return undefined;

  // 2) displayOrder가 작은(= 이전) 질문들만 골라서
  const previousProps = allProps.filter(
    (prop) => prop.displayOrder < current.displayOrder
  );

  // 3) 뒤에서부터 순회하며, options에 parentUids가 포함된 질문을 찾는다
  for (let i = previousProps.length - 1; i >= 0; i--) {
    const prev = previousProps[i];
    if (prev.options?.some((option) => parentUids.has(option.uid))) {
      return prev.no?.toString();
    }
  }

  return undefined;
}

/**
 * ILineDemandSurvey 를 받아 questionProps 기반으로 IFilter 배열 생성
 */
export function convertLineDemandSurveyToFilters(
  survey: ILineDemandSurvey,
  answer?: ILineDemandSurveyAnswer,
  userInfo?: IUserInfo
): IFilter[] {
  if (!survey.questionProps) {
    return [];
  }

  // answerProps를 no → IAnswerProp 으로 매핑
  const answerMap = new Map<number, IAnswerProp>();
  answer?.answerProps?.forEach((ap) => answerMap.set(ap.no, ap));

  // 질문을 displayOrder 순으로 정렬
  const sortedProps = survey.questionProps
    .slice() // 원본 유지
    .sort(
      (a: IQuestionProp, b: IQuestionProp) => a.displayOrder - b.displayOrder
    );

  return sortedProps.map((questionProp) => {
    const options: IOption[] = questionProp.options?.map(mapOption) ?? [];

    // 매칭된 답변이 있는지 확인
    const answerProp = answerMap.get(questionProp.no);
    let selectedOptions: IOption | IOption[] | undefined = undefined;

    if (answerProp) {
      // 단일 선택 가정
      if (answerProp.selected && options.length > 0) {
        const found = options.find((opt) => opt.key === answerProp.selected);
        if (found) {
          selectedOptions = found;
        }
      } else if (answerProp.inputValue) {
        selectedOptions = {
          key: answerProp.inputValue,
          value: answerProp.inputValue,
          name: answerProp.inputValue,
        } as IOption;
      }
    } else if (questionProp.autoFillSourceKey?.length) {
      if (userInfo) {
        // selectedOptions에 자동입력 필드 데이터 매칭
        const data = userInfo[questionProp.autoFillSourceKey];
        selectedOptions = {
          key: data,
          value: data,
          name: data,
        };
      }
    }

    return {
      key: questionProp.no.toString(),
      type: FilterKeyValueType.FilterKey_OptionKey,
      label: questionProp.label,
      options,
      availableOptions: options,
      fieldType: questionProp.type,
      parentKey: findParentKey(sortedProps, questionProp),
      dummyOptionName: `${questionProp.label}`,
      essential: questionProp.requiredYn,
      selectedOptions,
      isAutoFilled: !!questionProp.autoFillSourceKey?.length,
    } as IFilter;
  });
}
